import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import {
  forgotPasswordFormDefaultValue,
  forgotPasswordFormType,
  forgotPasswordFormValidationSchema,
} from './utils';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Input } from 'components/ui/input';
import { useForgotPassword } from '../../hooks/use-auth';
import { Button } from 'components/ui/button';
import { SetStateAction, useEffect, useRef, useState } from 'react';
import { CaptchaRef } from 'features/captcha/index.type';
import { Captcha } from 'features/captcha';
import { useTranslation } from 'react-i18next';
import { useConfig } from 'hooks/use-config';
import { cn } from 'lib/utils';
import { useCaptchaConfig } from '../../hooks/use-captcha-config';
import { useTheme } from 'components/core/theme-provider';

/**
 * ForgotPasswordForm Component
 *
 * A form component that handles the password recovery process with email validation,
 * reCAPTCHA integration, and form submission handling.
 *
 * Features:
 * - Email input with validation
 * - Dynamic reCAPTCHA display based on email input status
 * - Form state management with React Hook Form and Zod validation
 * - Asynchronous form submission with loading state
 * - Error handling with captcha reset
 * - Navigation to confirmation page on successful submission
 * - Conditional button enabling based on form validity
 *
 * @returns {JSX.Element} The rendered form component with email input, captcha, and action buttons
 *
 * @example
 * // Basic usage
 * <ForgotPasswordForm />
 *
 * // Within a password recovery page
 * <div className="auth-container">
 *   <h1>Reset Password</h1>
 *   <p>Enter your email to receive a password reset link</p>
 *   <ForgotPasswordForm />
 * </div>
 */

export const ForgotpasswordForm = () => {
  const { t } = useTranslation();
  const { resolvedTheme } = useTheme();
  const captchaTheme = resolvedTheme === 'dark' ? 'dark' : 'light';
  const navigate = useNavigate();
  const form = useForm<forgotPasswordFormType>({
    defaultValues: forgotPasswordFormDefaultValue,
    resolver: zodResolver(forgotPasswordFormValidationSchema),
  });
  const { isPending, mutateAsync } = useForgotPassword();

  const captchaRef = useRef<CaptchaRef>(null);

  const resetCaptcha = () => {
    captchaRef.current?.reset();
  };

  const [captchaToken, setCaptchaToken] = useState('');
  const [showCaptcha, setShowCaptcha] = useState(false);
  const { configurationSettings } = useConfig();
  const { data: captchaConfig, isLoading: isCaptchaConfigLoading } = useCaptchaConfig();

  const isCaptchaEnabled = Boolean(
    captchaConfig?.captcha && Object.keys(captchaConfig.captcha).length > 0
  );

  const klaxInputClassName =
    '!h-[54px] rounded-xl border-[#d1d1d1] px-5 !text-base font-normal leading-[1.6] text-black shadow-none placeholder:text-[#888888] focus-visible:ring-0 dark:border-[#3d3d3d] dark:bg-[#19191c] dark:text-true-white dark:placeholder:text-[#b0b0b0]';
  const klaxLabelClassName = 'text-base font-medium leading-[1.2] text-black dark:text-true-white';
  const klaxFieldClassName = 'space-y-2.5';

  const emailValue = form.watch('email');

  useEffect(() => {
    if (isCaptchaEnabled && emailValue && emailValue.trim() !== '') {
      setShowCaptcha(true);
    } else {
      setShowCaptcha(false);
      if (captchaToken) {
        setCaptchaToken('');
      }
    }
  }, [isCaptchaEnabled, emailValue, captchaToken]);

  const handleCaptchaVerify = (token: SetStateAction<string>) => {
    setCaptchaToken(token);
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken('');
  };

  const onSubmitHandler = async (values: forgotPasswordFormType) => {
    if (isCaptchaEnabled && !captchaToken) {
      return;
    }

    try {
      await mutateAsync({
        email: values.email,
        captchaCode: captchaToken || '',
      });

      navigate('/sent-email');
    } catch (_error) {
      resetCaptcha();
    }
  };

  const emailError = form.formState.errors.email;

  const isEmailValid = emailValue && emailValue.trim() !== '' && !emailError;

  const isButtonDisabled = 
    isPending || 
    isCaptchaConfigLoading || 
    !isEmailValid || 
    (isCaptchaEnabled && !captchaToken);

  return (
    <div className="w-full">
      <Form {...form}>
        <form className="flex w-full flex-col gap-8" onSubmit={form.handleSubmit(onSubmitHandler)}>
          <div className="flex w-full flex-col gap-6">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className={klaxFieldClassName}>
                  <FormLabel className={klaxLabelClassName}>{t('EMAIL')}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder={t('ENTER_YOUR_EMAIL_ADDRESS', 'Enter your email address')}
                      className={klaxInputClassName}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex w-full flex-col gap-6">
            {showCaptcha && captchaConfig?.captcha && (
              <div className="flex w-full justify-center">
                <Captcha
                  type="reCaptcha"
                  siteKey={captchaConfig.captcha.key}
                  theme={captchaTheme}
                  onVerify={handleCaptchaVerify}
                  onExpired={handleCaptchaExpired}
                  size="normal"
                />
              </div>
            )}

            <Button
              type="submit"
              variant="outline"
              className={cn(
                'flex h-[50px] w-full items-center justify-center gap-2 rounded-full border-[#d1d1d1] bg-white px-6 py-3 text-sm font-medium leading-[1.2] text-black shadow-none sm:h-[54px] sm:gap-[12px] sm:px-10 sm:py-3.5 sm:text-base',
                'hover:bg-[#f5f5f5] hover:text-black',
                'dark:border-true-white dark:bg-true-white dark:text-black dark:hover:bg-true-white dark:hover:text-black'
              )}
              disabled={isButtonDisabled}
              loading={isPending}
            >
              {t('SEND_RESET_LINK')}
            </Button>

            <p className="text-center text-base leading-[1.6] text-[#5d5d5d] dark:text-[#e7e7e7]">
              {t('REMEMBERED_YOUR_PASSWORD', 'Remembered your password?')}{' '}
              <Link to="/login" className="font-semibold leading-[1.2] text-black hover:underline dark:text-true-white">
                {t('GO_TO_LOG_IN', 'Log in')}
              </Link>
            </p>
          </div>
        </form>
      </Form>
    </div>
  );
};
